import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  private eragon: string = 'AB!!CDEF=GHI&JKLMNOPQR#STUVW@XYZ012345*6789';
  public enumerator: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor() {}

  public async setEnumerator() {
    this.generateSessionKey().then((key) => {
      this.exportSessionKey(key).then((keyString) => {
        this.enumerator.next(keyString);
      });
    });
  }

  /** The encryption IV string generated through known indices of @eragon */
  private _x() {
    return [8, 15, 13, 39, 22, 32, 38, 27, 19, 9, 16, 5, 15, 4, 39, 33]
      .map((v) => this.eragon.charAt(v))
      .join('');
  }

  /** The encryption Key string generated through known indices of @eragon */
  private _y() {
    return [28, 17, 30, 20, 35, 31, 12, 8, 21, 11, 12, 26, 36, 18, 16, 2]
      .map((v) => this.eragon.charAt(v))
      .join('');
  }

  // private nodeRSA = new NodeRSA(environment.DXR);

  encryptData(data: string): string {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        data,
        CryptoJS.enc.Utf8.parse(this._y()),
        {
          iv: CryptoJS.enc.Utf8.parse(this._x()),
        }
      );

      // Return the encrypted data as a string
      return encrypted.toString();
    } catch (error) {
      return data; // Return original data if encryption fails
    }
  }

  decryptData(data: string): string {
    try {
      // Decrypt the data
      const decrypted = CryptoJS.AES.decrypt(
        data,
        CryptoJS.enc.Utf8.parse(this._y()),
        {
          iv: CryptoJS.enc.Utf8.parse(this._x()),
        }
      );

      // Convert the decrypted data to a UTF-8 string
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      return data; // Return original data if decryption fails
    }
  }

  encrypt(text: string, key: string): string {
    // Parse the key as Base64 (matching your .NET code)
    const parsedKey = CryptoJS.enc.Base64.parse(key);

    return CryptoJS.AES.encrypt(text, parsedKey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    }).toString();
  }

  decrypt(encryptedText: string, key: string): string {
    // Parse the key as Base64 (matching your .NET code)
    const parsedKey = CryptoJS.enc.Base64.parse(key);

    const decrypted = CryptoJS.AES.decrypt(encryptedText, parsedKey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  async importRSAPublicKey(publicKeyString: string): Promise<CryptoKey> {
    let binaryDer: Uint8Array;
    // Check if it's in PEM format
    // if (publicKeyString.includes('-----BEGIN PUBLIC KEY-----')) {
    // PEM format
    // const pemHeader = '-----BEGIN RSA PUBLIC KEY-----';
    // const pemFooter = '-----END RSA PUBLIC KEY-----';
    // const pemContents = publicKeyString
    //   .replace(pemHeader, '')
    //   .replace(pemFooter, '')
    //   .replace(/\s/g, '');

    const binaryDerString = atob(publicKeyString);
    binaryDer = new Uint8Array(binaryDerString.length);
    for (let i = 0; i < binaryDerString.length; i++) {
      binaryDer[i] = binaryDerString.charCodeAt(i);
    }
    // } else {
    //   // raw base64
    //   console.log('RAW BASE64', publicKeyString);
    //   const binaryDerString = atob(publicKeyString);
    //   console.log('AFTER ATOB', binaryDerString);
    //   binaryDer = new Uint8Array(binaryDerString.length);
    //   for (let i = 0; i < binaryDerString.length; i++) {
    //     binaryDer[i] = binaryDerString.charCodeAt(i);
    //   }
    // }

    return await crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt']
    );
  }

  async encryptWithRSAPublicKey(
    text: string,
    publicKey: CryptoKey
  ): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      data
    );

    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  async encryptKey(key: string): Promise<string | false> {
    // const pk = environment.DXR;
    // const publicKey = await this.importRSAPublicKey(pk);
    // console.log('PKEY', publicKey);
    // return await this.encryptWithRSAPublicKey(key, publicKey);
    const encryptor = new JSEncrypt();
    // encryptor.setPublicKey(environment.DXR.split(' ')[0].trim());
    encryptor.setPublicKey(environment.DXR);
    return encryptor.encrypt(key);
  }

  // encryptKey(key: string) {
  //   const pk = environment.DXR;
  //   return this.nodeRSA.encryptStringWithRsaPublicKey({
  //     text: key,
  //     publicKey: pk,
  //   });
  // }

  async generateSessionKey(length: 128 | 192 | 256 = 256): Promise<CryptoKey> {
    try {
      const key = await crypto.subtle.generateKey(
        {
          name: 'AES-CBC',
          length: length,
        },
        true,
        ['encrypt', 'decrypt']
      );
      return key;
    } catch (error) {
      // console.error('Error generating AES key:', error);
      throw error;
    }
  }

  async exportSessionKey(key: CryptoKey): Promise<string> {
    try {
      const exported = await crypto.subtle.exportKey('raw', key);
      // Convert ArrayBuffer to base64 string
      return btoa(String.fromCharCode(...new Uint8Array(exported)));
    } catch (error) {
      // console.error('Error exporting session key:', error);
      throw error;
    }
  }
}
